import type { UIField } from '../../components/field/ui-field';
import { UITable } from '../../components/table/ui-table';
import { UITooltip } from '../../components/tooltip/ui-tooltip';
import { ComponentState } from '../../components/ui-element.const';
import type { ComponentPlugin } from '../../components/ui-element.type';
import { browser } from '../../global/browser';
import { Digest } from '../../global/digest';
import { createElement, prerenderElementsTree } from '../../global/render-api';
import { findNodesBetween } from '../../global/ui-helpers';

/**
 * @memberof SharedComponents
 * @classdesc Class for ui-table invert plugin.
 * @alias UITableInvert
 * @implements {ComponentPlugin}
 * @param {HTMLTableElement} target Target instance.
 */
export class UITableInvert implements ComponentPlugin {
    private readonly target: UITable;
    private maxRowSpan: number = 1;
    private observer!: MutationObserver;

    constructor(target: UITable) {
        this.target = target;
    }

    /**
     * Checks if table cell contains input field.
     * @param {HTMLTableCellElement} td
     * @returns {boolean}
     * @private
     */
    private doesCellContainField(td: HTMLTableCellElement): boolean {
        const allowedElems = ['ui-field', 'input', 'select', 'textarea'];
        return td.querySelectorAll(allowedElems.join(',')).length > 0;
    }

    /**
     * Inserts label into cell element directly with span.
     * @param {HTMLTableCellElement} td
     * @param {NodeListOf<ChildNode>} childNodes
     * @param {HTMLDivElement} container
     * @private
     */
    private insertLabelIntoCell(
        td: HTMLTableCellElement,
        childNodes: NodeListOf<ChildNode>,
        container: HTMLDivElement
    ): void {
        // Do not do anything for extra rows.
        if (td.parentElement?.classList.contains('-extra')) {
            return;
        }
        const nodes = Array.from(childNodes).map((child: ChildNode) => {
            prerenderElementsTree(child, true);
            const ch = child.cloneNode(true);
            if (ch instanceof UITooltip) {
                this.updateTooltipIds(ch);
            }
            return ch;
        });

        container.appendChild(
            createElement({
                tagName: 'span',
                attributes: {
                    class: 'cell-label',
                },
                children: nodes,
            })!
        );

        td.classList.add('col');
        if (this.hasOnlyTextualContent(td)) {
            td.classList.add('-has-text-only');
        }
        if (this.isEmptyCell(td)) {
            td.classList.add('-is-empty-field');
        }
        td.insertAdjacentElement('afterbegin', container);

        this.wrapCellValue(td, container);
    }

    /**
     * Wraps remaining cell content (after label container) in a span
     * to keep it grouped in mobile. Fixes right-alignment for cells
     * with multiple inline nodes (e.g. amount + currency span).
     * @param {HTMLTableCellElement} td
     * @param {HTMLDivElement} container
     * @private
     */
    private wrapCellValue(
        td: HTMLTableCellElement,
        container: HTMLDivElement
    ): void {
        if (this.doesCellContainField(td)) {
            return;
        }
        const remainingNodes = Array.from(td.childNodes).filter(
            (node) =>
                node !== container &&
                !(node.nodeType === Node.TEXT_NODE && !node.textContent?.trim())
        );
        if (remainingNodes.length > 1) {
            const valueWrapper = createElement({
                tagName: 'span',
                classList: {
                    'cell-value': true,
                },
            })!;
            remainingNodes.forEach((node) => valueWrapper.appendChild(node));
            td.appendChild(valueWrapper);
        }
    }

    /**
     * Checks if table hasn't element nodes.
     * @param {HTMLTableCellElement} td
     * @returns {boolean}
     * @private
     */
    private hasOnlyTextualContent(td: HTMLTableCellElement): boolean {
        return (
            Array.from(td.childNodes).filter(
                (node) => node.nodeType === Node.ELEMENT_NODE
            ).length === 0
        );
    }

    /**
     * Checks if table cell is empty (no elements and no text content).
     * @param {HTMLTableCellElement} td
     * @returns {boolean}
     * @private
     */
    private isEmptyCell(td: HTMLTableCellElement): boolean {
        const noElements =
            Array.from(td.childNodes).filter(
                (node) => node.nodeType === Node.ELEMENT_NODE
            ).length === 0;
        return noElements && !td.textContent?.trim();
    }

    /**
     * Inserts label content into a ui-field element within the cell.
     * @param {HTMLTableCellElement} td
     * @param {NodeListOf<ChildNode>} content
     * @private
     */
    private insertLabelIntoField(
        td: HTMLTableCellElement,
        content: NodeListOf<ChildNode>
    ): void {
        const allowedToClone = ['ui-tooltip', 'a'];
        const uiField = td.querySelector<UIField>('ui-field');
        const hasCheckbox = !!td.querySelector('input[type="checkbox"]');
        const hasRadio = !!td.querySelector('input[type="radio"]');
        if (!uiField && (hasCheckbox || hasRadio)) {
            try {
                this.insertLabelIntoCell(
                    td,
                    content,
                    this.createLabelContainer()
                );
            } catch (e) {
                // colspan is out of bounds of the table. Skip and do nothing.
            }
            return;
        }

        if (!uiField) {
            return;
        }

        let text = '';
        content.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
                text += child.textContent?.trim() ?? '';
                return;
            }

            const element = child as Element;
            if (allowedToClone.includes(element.localName)) {
                prerenderElementsTree(child, true);
                const newClone = child.cloneNode(true) as Element;
                if (newClone instanceof UITooltip) {
                    this.updateTooltipIds(newClone);
                }
                if (!uiField.state) {
                    newClone.setAttribute('slot', 'label');
                    uiField.appendChild(newClone);
                } else {
                    (uiField.labelContainer as HTMLDivElement).appendChild(
                        newClone
                    );
                }
            }
        });

        const labelText = text.trim();
        if (labelText && !uiField.getAttribute('label')) {
            // Keep label available even when property setters are not ready yet.
            uiField.setAttribute('label', labelText);
        }
        if (!uiField.label && labelText) {
            uiField.label = labelText;
        }
    }

    /**
     * @param {UITooltip} tooltip
     * @private
     */
    private updateTooltipIds(tooltip: UITooltip): void {
        const control = tooltip.querySelector('.ui-tooltip__control');
        const popover = tooltip.querySelector('.ui-tooltip__popover');
        const content = tooltip.querySelector('.ui-tooltip__content');
        const newPopoverId = Digest.randomId();
        control?.setAttribute('aria-controls', newPopoverId);
        popover?.setAttribute('id', newPopoverId);
        if (content) {
            content.setAttribute('id', tooltip.getContentId());
        }
    }

    /**
     * Prepares row for small screen transform.
     * @param {HTMLTableRowElement} row
     * @private
     */
    private prepareRow(row: HTMLTableRowElement): void {
        // If row already reverted do not do anything.
        if (row.dataset.inverted) {
            return;
        }
        this.maxRowSpan -= 1;
        const hasRemainingRowSpan = this.maxRowSpan > 0;
        const currentRowSpan = hasRemainingRowSpan ? 1 : 0;
        const thead = this.target.table.tHead;
        const shouldUseStateWorkaround = this.shouldUseStateWorkaround(row);
        if (shouldUseStateWorkaround) {
            this.stabilizeMatchingScope(row);
        }
        const cellsFromFinder = findNodesBetween(row, 'td, th', 'ui-table');
        const cells =
            cellsFromFinder.length || !shouldUseStateWorkaround
                ? cellsFromFinder
                : Array.from(row.cells);
        const ths = thead?.querySelectorAll('th');
        let nextSpan = 0;
        cells.forEach((cell: Element, i: number) => {
            const td = cell as HTMLTableCellElement;
            this.maxRowSpan = Math.max(this.maxRowSpan, td.rowSpan);
            const correctedIndex = i + nextSpan;
            if (td.colSpan > 0) {
                nextSpan += td.colSpan - 1;
            }
            if (this.doesCellContainField(td) && ths?.[correctedIndex]) {
                this.insertLabelIntoField(td, ths[correctedIndex].childNodes);
            } else if (ths?.[correctedIndex]) {
                const div = this.createLabelContainer();
                for (
                    let j = correctedIndex;
                    j < correctedIndex + td.colSpan;
                    j++
                ) {
                    try {
                        this.insertLabelIntoCell(
                            td,
                            // As opposed to tooltips, ideally ui-annotation should not be copied from header to body cells because it is only meant to appear once and not for each row. As of now it is still copied.
                            ths[j + currentRowSpan].childNodes,
                            div
                        );
                    } catch (e) {
                        // colspan is out of bounds table. Skip and do nothing.
                    }
                }
            }
        });
        row.dataset.inverted = '1';
    }

    /**
     * @private
     * @returns {HTMLDivElement}
     */
    private createLabelContainer(): HTMLDivElement {
        return createElement({
            tagName: 'div',
            classList: {
                '-cell-label-container': true,
            },
        })!;
    }

    /**
     * Prepares HTML markup. If cell has UIField insert label into
     * UIField, otherwise add additional span element to keep the label.
     * @private
     */
    private prepareLayout(): void {
        const tbody = this.target.table.tBodies[0];
        const thead = this.target.table.tHead;
        if (!tbody || !thead) {
            return;
        }
        const shouldUseStateWorkaround = this.shouldUseStateWorkaround(
            this.target.table
        );
        const rowsSelector =
            'tbody tr:not([data-inverted]), tfoot tr:not([data-inverted])';
        const rowsFromFinder = findNodesBetween(
            this.target.table,
            rowsSelector,
            'ui-table'
        );
        const rows =
            rowsFromFinder.length || !shouldUseStateWorkaround
                ? rowsFromFinder
                : this.getFallbackRows();
        rows.forEach(this.prepareRow.bind(this) as (row: Element) => void);
    }

    /**
     * Pre-renders dynamic custom elements that may mutate during matching.
     * @param {Element} root
     * @private
     */
    private stabilizeMatchingScope(root: Element): void {
        const skippedStates = [
            ComponentState.Rendered,
            ComponentState.Hydrated,
        ];

        root.querySelectorAll('ui-status').forEach((node) => {
            const state = node.getAttribute('state') as ComponentState;
            if (skippedStates.includes(state)) {
                return;
            }
            prerenderElementsTree(node, true);
        });
    }

    /**
     * Find elements that may mutate inner state which conflicts with isEqualNode solution.
     * Used to verify whether to use the getFallbackRows.
     * @param {Element} root
     * @returns {boolean}
     * @private
     */
    private hasContentWithState(root: Element): boolean {
        return !!root.querySelector(
            'ui-status, input[type="radio"], input[type="checkbox"]'
        );
    }

    /**
     * Is mobile and has stateful components
     * @param {Element} root
     * @returns {boolean}
     * @private
     */
    private shouldUseStateWorkaround(root: Element): boolean {
        return browser.mobileMediaMatches() && this.hasContentWithState(root);
    }

    /**
     * Used as a workaround to render row elements with state (hasContentWithState)
     * until findNodesBetween isEqualNode solution is replaced with one that
     * supports content with changing inner state.
     * @returns {Array<HTMLTableRowElement>}
     * @private
     */
    private getFallbackRows(): Array<HTMLTableRowElement> {
        return Array.from(this.target.table.tBodies)
            .flatMap((tableBody) => Array.from(tableBody.rows))
            .concat(
                this.target.table.tFoot
                    ? Array.from(this.target.table.tFoot.rows)
                    : []
            )
            .filter((tableRow) => !tableRow.dataset.inverted);
    }

    /**
     * @param {Array<MutationRecord>} mutations
     * @private
     */
    private handleMutations(mutations: Array<MutationRecord>): void {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (
                    node instanceof Element &&
                    node.closest('ui-table') !== this.target
                ) {
                    return;
                }
                if (!(node instanceof Element)) {
                    return;
                }
                switch (node.localName) {
                    case 'tr':
                        return this.prepareRow(node as HTMLTableRowElement);
                    case 'td':
                        return this.prepareRow(
                            (node as HTMLTableCellElement)
                                .parentElement as HTMLTableRowElement
                        );
                    case 'tbody':
                    case 'tfoot':
                    case 'thead':
                        return node
                            .querySelectorAll('tr')
                            .forEach((tr) => this.prepareRow(tr));
                }
            });
        });
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.target.table.classList.add('-invert');
        this.prepareLayout();
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.observer = new MutationObserver(this.handleMutations.bind(this));
        this.observer.observe(this.target.table, {
            childList: true,
            subtree: true,
        });
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.observer.disconnect();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.observer.observe(this.target.table, {
            childList: true,
            subtree: true,
        });
    }
}

UITable.registerPlugin('invert', UITableInvert);
